import React, { useEffect, useState } from 'react';
import {
  Card, Row, Col, Statistic, Typography, Spin, message, Space, Button, Table, Alert, Tag,
} from 'antd';
import {
  ToolOutlined, UserOutlined, CommentOutlined, HeartOutlined,
  StarOutlined, RiseOutlined, DownloadOutlined, EyeOutlined,
  ShoppingOutlined, MessageOutlined, GlobalOutlined, ReloadOutlined,
  EditOutlined, AppstoreOutlined, PictureOutlined, ApiOutlined, RocketOutlined,
} from '@ant-design/icons';
import { adminUsersAPI } from '../services/api';
import { useLanguage } from '../store/LanguageContext';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;

interface DashboardStats {
  totals: {
    tools: number;
    activeTools: number;
    featuredTools: number;
    users: number;
    reviews: number;
    favorites: number;
  };
  recent7d: {
    newTools: number;
    newUsers: number;
    newReviews: number;
  };
}

interface TrafficDashboard {
  today: string;
  todaySummary: { zhPv: number; enPv: number; eastChinaPv: number };
  peerConfigured: boolean;
  peerError: string | null;
  eastChinaReady: boolean;
  series: {
    zh: Array<{ date: string; pv: number; uv: number }>;
    en: Array<{ date: string; pv: number; uv: number }>;
    eastChina: Array<{ date: string; pv: number; uv: number }>;
  };
}

interface BusinessStats {
  orders: { total: number; paid: number; recent7d: number };
  feedback: { total: number; open: number; recent7d: number };
}

const AdminDashboardPage: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [traffic, setTraffic] = useState<TrafficDashboard | null>(null);
  const [business, setBusiness] = useState<BusinessStats | null>(null);

  const fetchAll = async (opts?: { refreshTraffic?: boolean }) => {
    const spinner = opts?.refreshTraffic ? setRefreshing : setLoading;
    spinner(true);
    try {
      const trafficParams = opts?.refreshTraffic
        ? { days: 14, refresh: 1 }
        : { days: 14 };
      const [dashRes, trafficRes, bizRes] = await Promise.all([
        adminUsersAPI.getDashboard(),
        adminUsersAPI.getTraffic(trafficParams).catch(() => null),
        adminUsersAPI.getBusinessStats().catch(() => null),
      ]);
      setStats(dashRes.data);
      if (trafficRes) setTraffic(trafficRes.data);
      if (bizRes) setBusiness(bizRes.data);
    } catch {
      message.error(t('adminPanel.fetchFailed'));
    } finally {
      spinner(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleExport = async (type: string, format: string) => {
    try {
      const res = await adminUsersAPI.exportData({ type, format });
      if (format === 'csv') {
        const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${type}-export.csv`;
        link.click();
        URL.revokeObjectURL(url);
      } else {
        const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${type}-export.json`;
        link.click();
        URL.revokeObjectURL(url);
      }
      message.success(t('admin.exportSuccess'));
    } catch {
      message.error(t('admin.exportFailed'));
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!stats) return null;

  const statCards = [
    {
      title: t('adminPanel.totalTools'),
      value: stats.totals.tools,
      icon: <ToolOutlined style={{ fontSize: 28, color: '#1677ff' }} />,
      color: '#e6f4ff',
      suffix: <Text type="success" style={{ fontSize: 12 }}>+{stats.recent7d.newTools} / 7d</Text>,
    },
    {
      title: t('adminPanel.activeTools'),
      value: stats.totals.activeTools,
      icon: <StarOutlined style={{ fontSize: 28, color: '#52c41a' }} />,
      color: '#f6ffed',
    },
    {
      title: t('adminPanel.featuredTools'),
      value: stats.totals.featuredTools,
      icon: <RiseOutlined style={{ fontSize: 28, color: '#faad14' }} />,
      color: '#fffbe6',
    },
    {
      title: t('adminPanel.totalUsers'),
      value: stats.totals.users,
      icon: <UserOutlined style={{ fontSize: 28, color: '#722ed1' }} />,
      color: '#f9f0ff',
      suffix: <Text type="success" style={{ fontSize: 12 }}>+{stats.recent7d.newUsers} / 7d</Text>,
    },
    {
      title: t('adminPanel.totalReviews'),
      value: stats.totals.reviews,
      icon: <CommentOutlined style={{ fontSize: 28, color: '#13c2c2' }} />,
      color: '#e6fffb',
      suffix: <Text type="success" style={{ fontSize: 12 }}>+{stats.recent7d.newReviews} / 7d</Text>,
    },
    {
      title: t('adminPanel.totalFavorites'),
      value: stats.totals.favorites,
      icon: <HeartOutlined style={{ fontSize: 28, color: '#eb2f96' }} />,
      color: '#fff0f6',
    },
  ];

  const exportItems = [
    { type: 'tools', label: t('adminPanel.exportTools') },
    { type: 'users', label: t('adminPanel.exportUsers') },
    { type: 'reviews', label: t('adminPanel.exportReviews') },
  ];

  const dateSet = new Set<string>();
  (traffic?.series.zh || []).forEach((r) => dateSet.add(r.date));
  (traffic?.series.en || []).forEach((r) => dateSet.add(r.date));
  (traffic?.series.eastChina || []).forEach((r) => dateSet.add(r.date));
  const dates = Array.from(dateSet).sort();
  const zhMap = new Map((traffic?.series.zh || []).map((r) => [r.date, r.pv]));
  const enMap = new Map((traffic?.series.en || []).map((r) => [r.date, r.pv]));
  const eastMap = new Map((traffic?.series.eastChina || []).map((r) => [r.date, r.pv]));
  const trafficRows = dates.map((date) => ({
    key: date,
    date,
    zhPv: zhMap.get(date) ?? 0,
    enPv: enMap.get(date) ?? 0,
    eastPv: eastMap.get(date) ?? 0,
  })).reverse();

  return (
    <div>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>{t('adminPanel.dashboardTitle')}</Title>
          <Text type="secondary">{t('adminPanel.opsSubtitle')}</Text>
        </div>
        <Space wrap>
          <Button
            icon={<ReloadOutlined />}
            loading={refreshing}
            onClick={() => fetchAll({ refreshTraffic: true })}
          >
            {t('adminPanel.refreshTraffic')}
          </Button>
          {exportItems.map(item => (
            <Button
              key={item.type}
              icon={<DownloadOutlined />}
              size="small"
              onClick={() => handleExport(item.type, 'csv')}
            >
              {item.label}
            </Button>
          ))}
        </Space>
      </div>

      {/* 1. 网站管理 */}
      <Card title={`1. ${t('adminPanel.siteManageTitle')}`} style={{ borderRadius: 12, marginBottom: 24 }}>
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          {t('adminPanel.siteManageNote')}
        </Text>
        <Space wrap size="middle">
          <Button type="primary" icon={<EditOutlined />} onClick={() => navigate('/admin/tools-manage')}>
            {t('adminPanel.toolManage')}
          </Button>
          <Button icon={<AppstoreOutlined />} onClick={() => navigate('/admin/categories')}>
            {t('adminPanel.categories')}
          </Button>
          <Button icon={<PictureOutlined />} onClick={() => navigate('/admin/assets')}>
            {t('adminPanel.assets')}
          </Button>
          <Button icon={<RocketOutlined />} onClick={() => navigate('/admin/submissions')}>
            {t('adminPanel.submissions')}
          </Button>
          <Button icon={<ApiOutlined />} href="/api-docs" target="_blank">
            {t('adminPanel.contentApiDocs')}
          </Button>
        </Space>
      </Card>

      {/* 2. 浏览量：中文版 / 英文版 / 华东 */}
      <Card
        title={`2. ${t('adminPanel.trafficTitle')}`}
        style={{ borderRadius: 12, marginBottom: 24 }}
        extra={traffic ? <Tag>{traffic.today}</Tag> : null}
      >
        {!traffic && (
          <Alert type="info" showIcon message={t('adminPanel.trafficUnavailable')} style={{ marginBottom: 16 }} />
        )}
        {traffic?.peerError && (
          <Alert type="warning" showIcon message={`${t('adminPanel.peerTrafficWarn')}: ${traffic.peerError}`} style={{ marginBottom: 16 }} />
        )}
        {traffic && !traffic.eastChinaReady && (
          <Alert type="info" showIcon message={t('adminPanel.eastChinaHint')} style={{ marginBottom: 16 }} />
        )}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={8}>
            <Card size="small" style={{ background: '#e6f4ff', border: 'none' }}>
              <Statistic
                title={t('adminPanel.zhPvToday')}
                value={traffic?.todaySummary.zhPv ?? 0}
                prefix={<EyeOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card size="small" style={{ background: '#f6ffed', border: 'none' }}>
              <Statistic
                title={t('adminPanel.enPvToday')}
                value={traffic?.todaySummary.enPv ?? 0}
                prefix={<GlobalOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card size="small" style={{ background: '#fff7e6', border: 'none' }}>
              <Statistic
                title={t('adminPanel.eastPvToday')}
                value={traffic?.todaySummary.eastChinaPv ?? 0}
                prefix={<RiseOutlined />}
              />
            </Card>
          </Col>
        </Row>
        <Table
          size="small"
          pagination={{ pageSize: 7 }}
          dataSource={trafficRows}
          columns={[
            { title: t('adminPanel.statDate'), dataIndex: 'date', key: 'date' },
            { title: t('adminPanel.zhPv'), dataIndex: 'zhPv', key: 'zhPv' },
            { title: t('adminPanel.enPv'), dataIndex: 'enPv', key: 'enPv' },
            { title: t('adminPanel.eastPv'), dataIndex: 'eastPv', key: 'eastPv' },
          ]}
          locale={{ emptyText: t('adminPanel.trafficEmpty') }}
        />
        <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
          {t('adminPanel.trafficSourceNote')}
        </Text>
      </Card>

      {/* 3. 业务数据：下单 / 意见 */}
      <Card title={`3. ${t('adminPanel.businessTitle')}`} style={{ borderRadius: 12, marginBottom: 24 }}>
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={6}>
            <Statistic
              title={t('adminPanel.ordersTotal')}
              value={business?.orders.total ?? 0}
              prefix={<ShoppingOutlined />}
            />
          </Col>
          <Col xs={12} sm={6}>
            <Statistic
              title={t('adminPanel.ordersPaid')}
              value={business?.orders.paid ?? 0}
            />
          </Col>
          <Col xs={12} sm={6}>
            <Statistic
              title={t('adminPanel.feedbackTotal')}
              value={business?.feedback.total ?? 0}
              prefix={<MessageOutlined />}
            />
          </Col>
          <Col xs={12} sm={6}>
            <Statistic
              title={t('adminPanel.feedbackOpen')}
              value={business?.feedback.open ?? 0}
            />
          </Col>
        </Row>
        <Space wrap style={{ marginTop: 16 }}>
          <Button onClick={() => navigate('/admin/orders')}>{t('adminPanel.orders')}</Button>
          <Button onClick={() => navigate('/admin/feedback')}>{t('adminPanel.feedback')}</Button>
        </Space>
        <Text type="secondary" style={{ display: 'block', marginTop: 12, fontSize: 12 }}>
          {t('adminPanel.businessNote')
            .replace('{orders7d}', String(business?.orders.recent7d ?? 0))
            .replace('{feedback7d}', String(business?.feedback.recent7d ?? 0))}
        </Text>
      </Card>

      {/* 4. 领域数据 */}
      <Title level={4} style={{ marginBottom: 16 }}>4. {t('adminPanel.domainDataTitle')}</Title>
      <Row gutter={[16, 16]}>
        {statCards.map((card, idx) => (
          <Col xs={24} sm={12} md={8} key={idx}>
            <Card
              hoverable
              style={{ borderRadius: 12 }}
              styles={{ body: { padding: '20px 24px' } }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <Text type="secondary" style={{ fontSize: 13 }}>{card.title}</Text>
                  <Statistic
                    value={card.value}
                    valueStyle={{ fontSize: 28, fontWeight: 700 }}
                    style={{ marginTop: 4 }}
                  />
                  {card.suffix && <div style={{ marginTop: 4 }}>{card.suffix}</div>}
                </div>
                <div
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: 12,
                    background: card.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {card.icon}
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default AdminDashboardPage;
